// ─── batch-65 voorraad-besteladvies (FO-481) ────────────────────────────────
//
// Gebundeld dagelijks besteladvies per leverancier: per regel goedkeuren,
// aanpassen, afkeuren (met reden) of uitstellen; afronden maakt automatisch een
// InkoopOrder in IN_BESTELLING met een bestelbericht (Client-kant), een
// afgekeurde regel levert een minimumniveau-voorstel op.
//
// Datamodel: `BesteladviesRun` (één advies per leverancier per bestelronde),
// `BesteladviesRunRegel`, `BesteladviesAudit`, `BesteladviesInstelling` en
// `InkoopBestelbericht` (schema-sectie `// batch-65 voorraad-besteladvies`).
// De namen wijken bewust af van de pure reken-interface `Besteladvies` in
// @streep/utils (inkoop-besteltraject.ts) om een naamconflict te voorkomen.
//
// Eén bron voor API én formulieren: de Zod-schema's hieronder valideren zowel de
// request in de API als de invoer in de backoffice (inline veldfouten).

import { z } from 'zod'
import { idParam } from './id-validators'
import type { VoorraadEenheid } from './voorraad'

// ─── Statussen ───────────────────────────────────────────────────────────────

export const BESTELADVIES_RUN_STATUS = ['OPEN', 'AFGEROND', 'VERVALLEN'] as const
export type BesteladviesRunStatus = (typeof BESTELADVIES_RUN_STATUS)[number]

export const BESTELADVIES_RUN_STATUS_LABELS: Record<BesteladviesRunStatus, string> = {
  OPEN: 'Te beoordelen',
  AFGEROND: 'Afgerond',
  VERVALLEN: 'Vervallen',
}

export const BESTELADVIES_REGEL_STATUS = [
  'OPEN',
  'GOEDGEKEURD',
  'AANGEPAST',
  'AFGEKEURD',
  'UITGESTELD',
  'VERVALLEN',
  'BESTELD',
  'GELEVERD',
  'GEANNULEERD',
] as const
export type BesteladviesRegelStatus = (typeof BESTELADVIES_REGEL_STATUS)[number]

export const BESTELADVIES_REGEL_STATUS_LABELS: Record<BesteladviesRegelStatus, string> = {
  OPEN: 'Te beoordelen',
  GOEDGEKEURD: 'Goedgekeurd',
  AANGEPAST: 'Aangepast',
  AFGEKEURD: 'Afgekeurd',
  UITGESTELD: 'Uitgesteld',
  VERVALLEN: 'Vervallen',
  BESTELD: 'In bestelling',
  GELEVERD: 'Geleverd',
  GEANNULEERD: 'Geannuleerd',
}

export const BESTELADVIES_REGEL_STATUS_BADGE: Record<
  BesteladviesRegelStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  OPEN: 'warning',
  GOEDGEKEURD: 'success',
  AANGEPAST: 'success',
  AFGEKEURD: 'danger',
  UITGESTELD: 'secondary',
  VERVALLEN: 'secondary',
  BESTELD: 'info',
  GELEVERD: 'success',
  GEANNULEERD: 'secondary',
}

/** Regels die bij het afronden besteld worden. */
export const BESTELADVIES_REGEL_TE_BESTELLEN = ['GOEDGEKEURD', 'AANGEPAST'] as const satisfies readonly BesteladviesRegelStatus[]

/**
 * "Actieve" regels: te beoordelen of goedgekeurd in een open advies, of besteld
 * en nog niet geleverd. Per artikel bestaat hooguit één actieve regel (partiële
 * unieke index in de migratie): nooit een dubbel advies of een dubbele order.
 */
export const BESTELADVIES_REGEL_ACTIEF = ['OPEN', 'GOEDGEKEURD', 'AANGEPAST', 'BESTELD'] as const satisfies readonly BesteladviesRegelStatus[]

/** Statussen waarin de beoordelaar de regel nog kan wijzigen (advies nog OPEN). */
export const BESTELADVIES_REGEL_BEOORDEELBAAR = [
  'OPEN',
  'GOEDGEKEURD',
  'AANGEPAST',
  'AFGEKEURD',
  'UITGESTELD',
] as const satisfies readonly BesteladviesRegelStatus[]

export const BESTELADVIES_REGEL_BRON = ['CRON', 'HANDMATIG'] as const
export type BesteladviesRegelBron = (typeof BESTELADVIES_REGEL_BRON)[number]

export const BESTELADVIES_REGEL_BRON_LABELS: Record<BesteladviesRegelBron, string> = {
  CRON: 'Dagelijkse run',
  HANDMATIG: 'Handmatig toegevoegd',
}

export const MINIMUM_VOORSTEL_STATUS = ['OPEN', 'GEACCEPTEERD', 'GEWEIGERD', 'GEEN_WIJZIGING', 'VERVANGEN'] as const
export type MinimumVoorstelStatus = (typeof MINIMUM_VOORSTEL_STATUS)[number]

export const MINIMUM_VOORSTEL_STATUS_LABELS: Record<MinimumVoorstelStatus, string> = {
  OPEN: 'Voorstel open',
  GEACCEPTEERD: 'Geaccepteerd',
  GEWEIGERD: 'Geweigerd',
  GEEN_WIJZIGING: 'Geen wijziging nodig',
  VERVANGEN: 'Vervangen door een nieuwer voorstel',
}

export const INKOOP_BESTELBERICHT_STATUS = [
  'KLAARGEZET',
  'VERZONDEN',
  'MISLUKT',
  'HANDMATIG_VERSTUURD',
] as const
export type InkoopBestelberichtStatus = (typeof INKOOP_BESTELBERICHT_STATUS)[number]

export const INKOOP_BESTELBERICHT_STATUS_LABELS: Record<InkoopBestelberichtStatus, string> = {
  KLAARGEZET: 'Klaargezet, nog versturen',
  VERZONDEN: 'Verstuurd',
  MISLUKT: 'Versturen mislukt',
  HANDMATIG_VERSTUURD: 'Handmatig verstuurd',
}

export const INKOOP_BESTELBERICHT_STATUS_BADGE: Record<
  InkoopBestelberichtStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  KLAARGEZET: 'warning',
  VERZONDEN: 'success',
  MISLUKT: 'danger',
  HANDMATIG_VERSTUURD: 'success',
}

// ─── batch-69 magazijn-cijfers (FO-521 §C) — automatisering ─────────────────

/**
 * Hoe ver het besteladvies zelfstandig gaat. Default en nu actief:
 * GOEDKEURING_HANDMATIG. Bij ADVIES_ALLEEN gaat er niets naar de
 * goedkeuringenlijst en wordt er niets besteld zonder handeling in het
 * besteladvies-scherm; bij VOLLEDIG_AUTOMATISCH bestelt het systeem alleen
 * regels die aan alle automatische grenzen voldoen (`magAutomatischBestellen`).
 */
export const BESTELADVIES_AUTOMATISERING = ['ADVIES_ALLEEN', 'GOEDKEURING_HANDMATIG', 'VOLLEDIG_AUTOMATISCH'] as const
export type BesteladviesAutomatisering = (typeof BESTELADVIES_AUTOMATISERING)[number]

export const BESTELADVIES_AUTOMATISERING_LABELS: Record<BesteladviesAutomatisering, string> = {
  ADVIES_ALLEEN: 'Alleen advies (niets naar de goedkeuringenlijst)',
  GOEDKEURING_HANDMATIG: 'Goedkeuren via de goedkeuringenlijst',
  VOLLEDIG_AUTOMATISCH: 'Volledig automatisch (binnen de grenzen)',
}

export const BESTELADVIES_AUTOMATISERING_UITLEG: Record<BesteladviesAutomatisering, string> = {
  ADVIES_ALLEEN:
    'De run maakt adviesregels en meldt ze. Er gaat niets naar de goedkeuringenlijst en er wordt niets besteld zonder handeling in het besteladvies.',
  GOEDKEURING_HANDMATIG:
    'De run zet de adviesregels in de goedkeuringenlijst. Zodra alle regels van een advies beoordeeld zijn, rondt het systeem het af tot een inkooporder met bestelbericht. Zonder goedkeuring wordt niets besteld.',
  VOLLEDIG_AUTOMATISCH:
    'Regels met een bekende prijs, binnen de bedraggrenzen, bij een leverancier met automatisch bestellen en zonder open telverschil worden zelf goedgekeurd en besteld. De rest gaat naar de goedkeuringenlijst.',
}

export const BESTELADVIES_GOEDKEURINGSBRON = ['HANDMATIG', 'INBOX', 'AUTOMATISCH'] as const
export type BesteladviesGoedkeuringsbron = (typeof BESTELADVIES_GOEDKEURINGSBRON)[number]

export const BESTELADVIES_GOEDKEURINGSBRON_LABELS: Record<BesteladviesGoedkeuringsbron, string> = {
  HANDMATIG: 'Besteladvies-scherm',
  INBOX: 'Goedkeuringenlijst',
  AUTOMATISCH: 'Automatisch',
}

/** Waarom een adviesregel (nog) niet goedgekeurd kan worden (spec 221 §C11). */
export const BESTELADVIES_BLOKKADE_REDEN = ['GEEN_PRIJS', 'GEEN_LEVERANCIER'] as const
export type BesteladviesBlokkadeReden = (typeof BESTELADVIES_BLOKKADE_REDEN)[number]

export const BESTELADVIES_BLOKKADE_REDEN_LABELS: Record<BesteladviesBlokkadeReden, string> = {
  GEEN_PRIJS: 'Geen prijs: vul eerst de inkoopprijs van het artikel in',
  GEEN_LEVERANCIER: 'Geen leverancier: stel eerst een leverancier in',
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface MinimumVoorstelDTO {
  minimum: number
  /** batch-69 (FO-521 §B8) — voorgesteld maximum (aanvulniveau); null = geen. */
  maximum: number | null
  status: MinimumVoorstelStatus
  uitleg: string | null
  beslistOp: string | null
  beslistDoorNaam: string | null
}

export interface BesteladviesRegelDTO {
  id: string
  runId: string
  artikelId: string
  status: BesteladviesRegelStatus
  bron: BesteladviesRegelBron
  artikelNummer: string
  artikelNaam: string
  leverancierArtNr: string | null
  eenheid: VoorraadEenheid
  saldo: number
  gereserveerd: number
  beschikbaar: number
  minimumVoorraad: number
  maximumVoorraad: number | null
  inBestelling: number
  /**
   * Concept-orders voor dit artikel (nog niet besteld): ze tellen NIET mee in
   * "in bestelling", maar staan erbij zodat niemand dubbel bestelt.
   */
  conceptOrders: ArtikelConceptOrderDTO[]
  /**
   * Wat er NU onderweg is (live, zelfde maat als `inBestelling`); alleen voor
   * regels die nog besteld kunnen worden in een open advies, anders null. Hoger
   * dan `inBestelling` = er is sinds het advies al besteld of ontvangen.
   */
  nuOnderweg: number | null
  /** Live: langer dan 30 dagen ontvangen en nooit geboekt (telt niet mee); null zoals `nuOnderweg`. */
  nuOudOntvangen: number | null
  /**
   * Er staat een geldig maximum (op of boven het minimum). Zonder vult het
   * advies alleen aan tot het minimum (de gedeelde aanvul-regel).
   */
  heeftMaximum: boolean
  bestelAantal: number | null
  verbruik90d: number
  levertijdDagen: number | null
  adviesAantal: number
  aangepastAantal: number | null
  /** Wat er bij goedkeuren besteld wordt: het aangepaste aantal, anders het advies. */
  teBestellenAantal: number
  /** Laatste inkoopprijs per eenheid (excl. btw); null = onbekend. */
  inkoopPrijs: number | null
  prijsBron: 'ORDER' | 'ARTIKEL' | null
  /** teBestellenAantal x inkoopPrijs (null zonder prijs). */
  regelBedrag: number | null
  opmerking: string | null
  afkeurReden: string | null
  beoordeeldOp: string | null
  beoordeeldDoorNaam: string | null
  vervallenReden: string | null
  inkoopOrderId: string | null
  poNummer: string | null
  besteldAantal: number | null
  besteldOp: string | null
  geleverdAantal: number
  geleverdOp: string | null
  voorstel: MinimumVoorstelDTO | null
  /** De leverancier die nu op het artikel staat (kan afwijken van het advies). */
  artikelLeverancierId: string | null
  artikelLeverancierNaam: string | null
  /** Vrije-tekst-leverancier op het artikel (legacy), als hint. */
  leverancierTekst: string | null
  /** Eenduidige naam-match van `leverancierTekst` op een leverancier-relatie. */
  leverancierSuggestie: { id: string; naam: string } | null
  /** batch-69 (FO-521 §C11) — waarom deze regel (nog) niet goedgekeurd kan worden; null = kan. */
  blokkadeReden: BesteladviesBlokkadeReden | null
  /** batch-69 (FO-521 §7) — langs welke weg hij is goedgekeurd; null = niet goedgekeurd. */
  goedkeuringsbron: BesteladviesGoedkeuringsbron | null
  /** batch-69 (FO-521 §6.5) — er staat een open telverschil op het artikel (FO-467/FO-520). */
  openTelverschil: boolean
}

export interface BesteladviesRunTotalenDTO {
  aantalRegels: number
  perStatus: Record<BesteladviesRegelStatus, number>
  /** Regels met status OPEN. */
  teBeoordelen: number
  /** OPEN + GOEDGEKEURD + AANGEPAST: wat er (nog) besteld kan worden. */
  teBestellen: number
  /** Som van regelBedrag over de te-bestellen-regels (excl. btw). */
  totaalBedrag: number
  /** Te-bestellen-regels zonder bekende prijs (niet in het totaal). */
  regelsZonderPrijs: number
}

export interface BesteladviesRunDTO {
  id: string
  nummer: string
  status: BesteladviesRunStatus
  leverancierId: string | null
  leverancierNaam: string | null
  /** true = aangemaakt door de dagelijkse run. */
  doorRun: boolean
  createdAt: string
  laatstBijgewerktOp: string
  afgerondOp: string | null
  afgerondDoorNaam: string | null
  inkoopOrderId: string | null
  poNummer: string | null
  orderStatus: string | null
  totalen: BesteladviesRunTotalenDTO
  /** Dagen van afronden tot de laatste levering (null zolang niet alles geleverd). */
  doorlooptijdDagen: number | null
  /**
   * batch-69 (FO-521 §C13) — doorlooptijd per stap in kalenderdagen: advies ->
   * (laatste) goedkeuring -> besteld -> (laatste) levering. null = stap nog niet bereikt.
   */
  doorlooptijd: {
    adviesTotGoedkeuring: number | null
    goedkeuringTotBesteld: number | null
    besteldTotGeleverd: number | null
  }
  /** batch-69 (FO-521 §C10) — alles beoordeeld maar automatisch afronden lukte niet: de reden. */
  autoAfrondenBlokkade: string | null
}

export interface BesteladviesLeverancierDTO {
  id: string
  naam: string
  /** bestelEmail, anders het algemene mailadres; null = geen adres bekend. */
  bestelEmail: string | null
  bestelBerichtAutomatisch: boolean
  levertijdDagen: number | null
  /** Bestel-gate (FO-002): mag er nu bij deze leverancier besteld worden? */
  bestelbaar: boolean
  bestelbaarMelding: string | null
}

export interface InkoopBestelberichtDTO {
  id: string
  inkoopOrderId: string
  poNummer: string
  orderStatus: string
  status: InkoopBestelberichtStatus
  ontvanger: string | null
  onderwerp: string
  body: string
  automatisch: boolean
  pogingen: number
  laatsteFout: string | null
  verzondenOp: string | null
  verzondenDoorNaam: string | null
}

export interface BesteladviesRunDetailDTO extends BesteladviesRunDTO {
  opmerking: string | null
  leverancier: BesteladviesLeverancierDTO | null
  bestelbericht: InkoopBestelberichtDTO | null
  regels: BesteladviesRegelDTO[]
  regelsMeta: { page: number; pageSize: number; total: number }
  /** Kan het advies nu afgerond worden, en zo niet: waarom niet (zichtbaar). */
  kanAfronden: boolean
  afrondBlokkade: string | null
  /** batch-69: instelling "bestelbericht handmatig" (dan wordt het bericht bij afronden klaargezet). */
  bestelberichtHandmatig: boolean
}

export interface BesteladviesRunResultaatDTO {
  gestartOp: string
  /** Artikelen met een ingesteld minimum die de run bekeek. */
  beoordeeldeArtikelen: number
  nieuweRegels: number
  heropendeRegels: number
  ververstRegels: number
  vervallenRegels: number
  verplaatsteRegels: number
  overgeslagenOnderdrukt: number
  overgeslagenActief: number
  overgeslagenAfgekeurd: number
  /** Adviezen (leveranciers) die in deze run regels kregen. */
  adviezenMetNieuweRegels: number
  meldingen: number
  plafondGeraakt: boolean
}

export interface BesteladviesInstellingenDTO {
  actief: boolean
  runTijd: string
  alleenWerkdagen: boolean
  stilteperiodeDagen: number
  voorstelExtraDekkingDagen: number
  standaardLevertijdDagen: number
  standaardEntityId: string | null
  standaardEntityNaam: string | null
  laatsteRunDatum: string | null
  laatsteRunOp: string | null
  laatsteRunResultaat: BesteladviesRunResultaatDTO | null
  // batch-69 magazijn-cijfers (FO-521 §C10/§C13)
  automatiseringsniveau: BesteladviesAutomatisering
  automatiseringIngesteldDoorNaam: string | null
  automatiseringIngesteldOp: string | null
  bestelberichtHandmatig: boolean
  autoBestelMaxBedragPerRegel: number | null
  autoBestelMaxBedragPerRun: number | null
  herinneringNaWerkdagen: number
  escalatieNaWerkdagen: number
  /** Mag de gebruiker de automatiseringsstand en de auto-bestelgrenzen wijzigen (ADMIN)? */
  magAutomatiseringWijzigen: boolean
}

export interface ArtikelOpenOrderDTO {
  orderId: string
  poNummer: string
  status: string
  aantalOpen: number
  verwachtDatum: string | null
}

/** Een concept-order (CONCEPT / wacht op goedkeuring) met dit artikel: nog niet besteld. */
export interface ArtikelConceptOrderDTO {
  orderId: string
  poNummer: string
  status: string
  aantal: number
}

export interface ArtikelBestelStatusDTO {
  artikelId: string
  eenheid: VoorraadEenheid
  minimumVoorraad: number
  maximumVoorraad: number | null
  saldo: number
  gereserveerd: number
  beschikbaar: number
  /** Besteld, nog niet ontvangen (open bestellingen). */
  inBestelling: number
  /** Ontvangen maar nog niet binnengeboekt (laatste 30 dagen: telt mee als onderweg). */
  ontvangenNietGeboekt: number
  /**
   * Langer dan 30 dagen geleden ontvangen en nooit binnengeboekt: telt NIET meer
   * mee (anders drukt een vergeten ontvangst het advies voorgoed), maar blijft
   * zichtbaar om na te lopen.
   */
  ontvangenNietGeboektOud: number
  /** Vroegste verwachte leverdatum van de open bestellingen (yyyy-mm-dd, NL). */
  verwachtOp: string | null
  openOrders: ArtikelOpenOrderDTO[]
  /** Concept-orders (nog niet besteld; tellen niet mee in "in bestelling"). */
  conceptOrders: ArtikelConceptOrderDTO[]
  actieveRegel: {
    regelId: string
    runId: string
    runNummer: string
    status: BesteladviesRegelStatus
    leverancierNaam: string | null
  } | null
  onderdruktTot: string | null
  laatsteVoorstel: (MinimumVoorstelDTO & { regelId: string; runNummer: string }) | null
  leverancierId: string | null
  leverancierNaam: string | null
  /** batch-69 (FO-521 §C10) — dit artikel wordt nooit automatisch besteld. */
  autoBestellenUitgesloten: boolean
}

export const MINIMUMNIVEAU_WEERGAVE = ['VOORSTELLEN', 'ALLE', 'ZONDER_MINIMUM'] as const
export type MinimumniveauWeergave = (typeof MINIMUMNIVEAU_WEERGAVE)[number]

// batch-69 (FO-521 §B7) — het scherm heet nu "Niveaus" (minimum + maximum).
// ZONDER_MINIMUM = "Zonder niveau": een actief bulk-artikel zonder minimum of
// zonder maximum (de waarde van de enum blijft voor de bestaande links).
export const MINIMUMNIVEAU_WEERGAVE_LABELS: Record<MinimumniveauWeergave, string> = {
  VOORSTELLEN: 'Voorstellen',
  ALLE: 'Alle niveaus',
  ZONDER_MINIMUM: 'Zonder niveau',
}

export interface MinimumniveauRowDTO {
  artikelId: string
  artikelNummer: string
  artikelNaam: string
  eenheid: VoorraadEenheid
  minimumVoorraad: number
  maximumVoorraad: number | null
  bestelAantal: number | null
  beschikbaar: number
  verbruik90d: number
  levertijdDagen: number | null
  /** Minimum volgens de verbruiksformule (stelMinimumVoor). */
  berekendMinimum: number
  berekendUitleg: string
  /**
   * batch-69 (FO-521 §B8) — voorgesteld maximum: minimum + verbruik per levertijd
   * + besteleenheid (`stelMaximumVoor`), op basis van het huidige minimum (of het
   * berekende minimum als er nog geen minimum staat).
   */
  berekendMaximum: number
  /** batch-69 (FO-521 §B7) — huidige voorraad (totaal) en het advies op dit moment. */
  saldo: number
  adviesAantal: number
  traceerbaar: boolean
  bestelbaar: boolean
  openVoorstel: {
    regelId: string
    runNummer: string
    minimum: number
    maximum: number | null
    uitleg: string | null
    afkeurReden: string | null
    createdAt: string
  } | null
  laatsteWijziging: {
    op: string
    doorNaam: string | null
    van: number | null
    naar: number | null
  } | null
  onderdruktTot: string | null
}

export interface MinimumniveauLijstMetaDTO {
  page: number
  pageSize: number
  total: number
  /** Tellers voor de tabbladen (server-side over de volledige set). */
  aantalVoorstellen: number
  aantalZonderMinimum: number
  /** batch-69 — teller "Alle niveaus" (actieve artikelen met een minimum). */
  aantalAlle: number
}

export interface MinimumVoorstellenBulkResultaatDTO {
  geaccepteerd: number
  overgeslagen: Array<{ regelId: string; reden: string }>
}

export interface LeverancierBestelInstellingenDTO {
  leverancierId: string
  levertijdDagen: number | null
  bestelBerichtAutomatisch: boolean
  bestelEmail: string | null
  /**
   * Bestelmail-adres van de gekoppelde uitrusting-leverancier (Gereedschap >
   * Leveranciers): de eerste terugval als bestelEmail leeg is (één waarheid).
   */
  uitrustingEmail: string | null
  /** Het algemene mailadres van de relatie (laatste terugval). */
  algemeenEmail: string | null
  /** batch-69 (FO-521 §C10) — mag het besteladvies hier zelfstandig bestellen (bij VOLLEDIG_AUTOMATISCH)? */
  autoBestellen: boolean
  /** batch-69 (FO-521 §5) — mag de gebruiker zelfstandig bestellen AANzetten (ADMIN)? Uitzetten mag altijd. */
  magAutoBestellenAanzetten: boolean
}

// ─── Zod: requests (gedeeld API + form) ──────────────────────────────────────

const aantalSchema = z
  .number({ error: 'Vul een aantal in' })
  .int('Alleen hele aantallen')
  .min(1, 'Minimaal 1')
  .max(100000, 'Maximaal 100.000')

const opmerkingSchema = z.string().trim().max(1000, 'Maximaal 1000 tekens')

/**
 * De status die de gebruiker op zijn scherm zag. Is de regel intussen gewijzigd
 * (door de run, de herstel-tak of een collega), dan weigert de API met 409 in
 * plaats van over die wijziging heen te beslissen. Optioneel voor andere clients.
 */
const verwachteStatusSchema = z.enum(BESTELADVIES_REGEL_STATUS).optional()

/**
 * batch-69 (FO-521 §7) — langs welke weg de beoordeling binnenkomt: het
 * besteladvies-scherm (default) of de goedkeuringenlijst (`INBOX`). Wordt als
 * goedkeuringsbron op de regel vastgelegd (export: kolom automatiseringsbron).
 */
const beoordelingsBronSchema = z.enum(['HANDMATIG', 'INBOX']).optional()

/** Beoordeling van één adviesregel. */
export const besteladviesRegelActieSchema = z.discriminatedUnion('actie', [
  z.object({
    actie: z.literal('GOEDKEUREN'),
    opmerking: opmerkingSchema.optional(),
    verwachteStatus: verwachteStatusSchema,
    bron: beoordelingsBronSchema,
  }),
  z.object({
    actie: z.literal('AANPASSEN'),
    aantal: aantalSchema,
    opmerking: opmerkingSchema.optional(),
    verwachteStatus: verwachteStatusSchema,
    bron: beoordelingsBronSchema,
  }),
  z.object({
    actie: z.literal('AFKEUREN'),
    reden: z.string().trim().min(3, 'Geef een reden op (minimaal 3 tekens)').max(1000, 'Maximaal 1000 tekens'),
    opmerking: opmerkingSchema.optional(),
    verwachteStatus: verwachteStatusSchema,
    bron: beoordelingsBronSchema,
  }),
  z.object({
    actie: z.literal('UITSTELLEN'),
    opmerking: opmerkingSchema.optional(),
    verwachteStatus: verwachteStatusSchema,
    bron: beoordelingsBronSchema,
  }),
  z.object({ actie: z.literal('HEROPENEN'), verwachteStatus: verwachteStatusSchema }),
  z.object({
    actie: z.literal('OPMERKING'),
    opmerking: opmerkingSchema.nullable(),
    verwachteStatus: verwachteStatusSchema,
  }),
])

/**
 * "Alles goedkeuren": `gezienOp` = `laatstBijgewerktOp` van het advies zoals het
 * op het scherm stond. Zijn er daarna regels bijgekomen, dan weigert de API
 * (niets goedkeuren wat niemand gezien heeft).
 */
export const besteladviesAllesGoedkeurenSchema = z.object({
  gezienOp: z.string().datetime({ offset: true }).optional(),
})
export type BesteladviesAllesGoedkeurenInput = z.infer<typeof besteladviesAllesGoedkeurenSchema>
export type BesteladviesRegelActieInput = z.infer<typeof besteladviesRegelActieSchema>

/** Een artikel handmatig aan een (open) besteladvies toevoegen. */
export const besteladviesRegelToevoegenSchema = z.object({
  artikelId: idParam('artikelId'),
  aantal: aantalSchema.optional(),
  opmerking: opmerkingSchema.optional(),
})
export type BesteladviesRegelToevoegenInput = z.infer<typeof besteladviesRegelToevoegenSchema>

/** "In besteladvies zetten" vanaf het artikel of de alerts (advies per leverancier). */
export const besteladviesArtikelInAdviesSchema = z.object({
  artikelId: idParam('artikelId'),
})

/** Leverancier instellen op het artikel van een regel zonder leverancier. */
export const besteladviesLeverancierInstellenSchema = z.object({
  leverancierId: idParam('leverancierId'),
})

export const besteladviesAfrondenSchema = z.object({
  opmerking: opmerkingSchema.optional(),
  /**
   * Afronden controleert of er sinds het advies al besteld of ontvangen is
   * (409 ONDERWEG_GEWIJZIGD met de artikelen). Met `true` bevestigt de gebruiker
   * dat hij dat gezien heeft en toch wil bestellen.
   */
  bevestigOnderweg: z.boolean().optional(),
})

/** `details` van een 409 ONDERWEG_GEWIJZIGD bij afronden. */
export interface AfrondOnderwegWijzigingDTO {
  artikelen: Array<{
    artikelId: string
    artikelNaam: string
    /** Onderweg volgens het advies. */
    inAdvies: number
    /** Nu onderweg (live). */
    nu: number
    /** Beschikbaar volgens het advies en nu (samen met onderweg: de dekking). */
    beschikbaarInAdvies: number
    beschikbaarNu: number
    /** De open bestellingen die er nu zijn. */
    poNummers: string[]
  }>
}

export const besteladviesInstellingenSchema = z.object({
  actief: z.boolean(),
  runTijd: z
    .string()
    .trim()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Tijdstip als uu:mm (bijvoorbeeld 09:00)'),
  alleenWerkdagen: z.boolean(),
  stilteperiodeDagen: z.number().int('Hele dagen').min(1, 'Minimaal 1 dag').max(365, 'Maximaal 365 dagen'),
  voorstelExtraDekkingDagen: z.number().int('Hele dagen').min(0, 'Minimaal 0').max(180, 'Maximaal 180 dagen'),
  standaardLevertijdDagen: z.number().int('Hele dagen').min(0, 'Minimaal 0').max(365, 'Maximaal 365 dagen'),
  standaardEntityId: idParam('standaardEntityId').nullable(),
})
export type BesteladviesInstellingenInput = z.infer<typeof besteladviesInstellingenSchema>

const bedragGrens = z
  .number({ error: 'Vul een bedrag in' })
  .min(0, 'Minimaal 0')
  .max(1_000_000, 'Maximaal 1.000.000')
  .multipleOf(0.01, 'Maximaal 2 decimalen')
  .nullable()

/**
 * batch-69 (FO-521 §C10/§C13) — de automatiseringsstand en de grenzen. Alleen
 * ADMIN wijzigt dit (de route dwingt dat af). De herinnering- en
 * escalatietermijnen staan hier ook: ze horen bij de goedkeuringsstap.
 */
export const besteladviesAutomatiseringSchema = z
  .object({
    automatiseringsniveau: z.enum(BESTELADVIES_AUTOMATISERING),
    bestelberichtHandmatig: z.boolean(),
    autoBestelMaxBedragPerRegel: bedragGrens,
    autoBestelMaxBedragPerRun: bedragGrens,
    herinneringNaWerkdagen: z.number().int('Hele werkdagen').min(1, 'Minimaal 1').max(30, 'Maximaal 30'),
    escalatieNaWerkdagen: z.number().int('Hele werkdagen').min(1, 'Minimaal 1').max(60, 'Maximaal 60'),
  })
  .superRefine((v, ctx) => {
    if (v.escalatieNaWerkdagen <= v.herinneringNaWerkdagen) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['escalatieNaWerkdagen'],
        message: 'De escalatie komt na de herinnering: kies een hoger aantal werkdagen',
      })
    }
    if (
      v.autoBestelMaxBedragPerRegel != null &&
      v.autoBestelMaxBedragPerRun != null &&
      v.autoBestelMaxBedragPerRun < v.autoBestelMaxBedragPerRegel
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['autoBestelMaxBedragPerRun'],
        message: 'De grens per advies mag niet lager zijn dan de grens per regel',
      })
    }
  })
export type BesteladviesAutomatiseringInput = z.infer<typeof besteladviesAutomatiseringSchema>

export const leverancierBestelInstellingenSchema = z.object({
  levertijdDagen: z
    .number({ error: 'Vul een aantal dagen in' })
    .int('Hele dagen')
    .min(0, 'Minimaal 0')
    .max(365, 'Maximaal 365 dagen')
    .nullable(),
  bestelBerichtAutomatisch: z.boolean(),
  bestelEmail: z.string().trim().email('Geen geldig e-mailadres').max(254).nullable(),
  /** batch-69 (FO-521 §C10) — optioneel: oudere clients sturen hem niet mee (blijft dan ongewijzigd). */
  autoBestellen: z.boolean().optional(),
})
export type LeverancierBestelInstellingenInput = z.infer<typeof leverancierBestelInstellingenSchema>

const minimumVeld = z.number().int('Hele aantallen').min(0, 'Minimaal 0').max(100000, 'Maximaal 100.000')

/** Beslissing op een minimumniveau-voorstel. */
export const minimumVoorstelBeslissingSchema = z
  .discriminatedUnion('beslissing', [
    z.object({
      beslissing: z.literal('ACCEPTEREN'),
      minimumVoorraad: minimumVeld,
      maximumVoorraad: minimumVeld.nullable().optional(),
    }),
    z.object({
      beslissing: z.literal('WEIGEREN'),
      toelichting: z.string().trim().max(500, 'Maximaal 500 tekens').optional(),
    }),
  ])
  .superRefine((v, ctx) => {
    if (
      v.beslissing === 'ACCEPTEREN' &&
      v.maximumVoorraad != null &&
      v.maximumVoorraad < v.minimumVoorraad
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maximumVoorraad'],
        message: 'Het maximum mag niet onder het minimum liggen',
      })
    }
  })
export type MinimumVoorstelBeslissingInput = z.infer<typeof minimumVoorstelBeslissingSchema>

export const minimumVoorstellenBulkSchema = z.object({
  regelIds: z.array(idParam('regelId')).min(1, 'Kies minimaal één voorstel').max(200, 'Maximaal 200 per keer'),
})

/**
 * Niveaus direct instellen (niveaus-scherm inline, "zonder niveau", artikel-
 * detail). batch-69 (FO-521 §B6/§6.1): `maximum >= minimum`, en bij een minimum
 * boven 0 is het maximum verplicht ("vul een maximum in", met voorstel in de UI).
 * `maximumVoorraad` weglaten = ongewijzigd laten (oudere clients); de service
 * toetst dan de samengevoegde stand.
 */
export const minimumInstellenSchema = z
  .object({
    minimumVoorraad: minimumVeld,
    maximumVoorraad: minimumVeld.nullable().optional(),
    /** Besteleenheid (verpakkingsgrootte); weglaten = ongewijzigd. */
    bestelAantal: z
      .number()
      .int('Hele aantallen')
      .min(1, 'Minimaal 1')
      .max(100000, 'Maximaal 100.000')
      .nullable()
      .optional(),
  })
  .superRefine((v, ctx) => {
    if (v.maximumVoorraad != null && v.maximumVoorraad < v.minimumVoorraad) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maximumVoorraad'],
        message: 'Het maximum mag niet onder het minimum liggen',
      })
    }
    if (v.minimumVoorraad > 0 && v.maximumVoorraad === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maximumVoorraad'],
        message: 'Vul een maximum in: tot dit niveau vult het besteladvies aan',
      })
    }
  })
export type MinimumInstellenInput = z.infer<typeof minimumInstellenSchema>

/**
 * batch-69 (FO-521 §B7) — bulk-bewerken per selectie: "zet minimum op X,
 * maximum op Y". Minstens één van beide; per artikel toetst de service de
 * samengevoegde stand (een artikel dat daardoor ongeldig zou worden, komt terug
 * met de reden en wordt niet gewijzigd).
 */
export const niveausBulkSchema = z
  .object({
    artikelIds: z.array(idParam('artikelId')).min(1, 'Kies minimaal één artikel').max(200, 'Maximaal 200 per keer'),
    minimumVoorraad: minimumVeld.optional(),
    maximumVoorraad: minimumVeld.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.minimumVoorraad === undefined && v.maximumVoorraad === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minimumVoorraad'],
        message: 'Vul een minimum en/of een maximum in',
      })
    }
    if (
      v.minimumVoorraad !== undefined &&
      v.maximumVoorraad !== undefined &&
      v.maximumVoorraad < v.minimumVoorraad
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maximumVoorraad'],
        message: 'Het maximum mag niet onder het minimum liggen',
      })
    }
  })
export type NiveausBulkInput = z.infer<typeof niveausBulkSchema>

export interface NiveausBulkResultaatDTO {
  gewijzigd: number
  overgeslagen: Array<{ artikelId: string; artikelNummer: string | null; reden: string }>
}

/**
 * batch-69 (FO-521 §B7) — Excel-import van niveaus: het tabblad "Niveaus" van de
 * waarde-export (kolommen Artikelnummer, Minimum, Maximum) teruguploaden. Eerst
 * een preview (`toepassen: false`): rijen die niet matchen of ongeldig zijn staan
 * rood en worden nooit doorgevoerd.
 */
export interface NiveausImportRegelDTO {
  rij: number
  artikelNummer: string
  artikelId: string | null
  artikelNaam: string | null
  huidigMinimum: number | null
  huidigMaximum: number | null
  nieuwMinimum: number | null
  nieuwMaximum: number | null
  status: 'WIJZIGING' | 'ONGEWIJZIGD' | 'FOUT'
  melding: string | null
}

export interface NiveausImportResultaatDTO {
  toegepast: boolean
  regels: NiveausImportRegelDTO[]
  aantalWijzigingen: number
  aantalOngewijzigd: number
  aantalFouten: number
}

/** Bestelbericht (opnieuw) versturen, eventueel met aangepaste tekst of adres. */
export const bestelberichtVersturenSchema = z.object({
  ontvanger: z.string().trim().email('Geen geldig e-mailadres').max(254),
  onderwerp: z.string().trim().min(3, 'Minimaal 3 tekens').max(300, 'Maximaal 300 tekens'),
  body: z.string().trim().min(10, 'Minimaal 10 tekens').max(20000, 'Maximaal 20.000 tekens'),
})
export type BestelberichtVersturenInput = z.infer<typeof bestelberichtVersturenSchema>

/** Bestelbericht buiten het systeem verstuurd (telefoon, webshop) afvinken. */
export const bestelberichtHandmatigSchema = z.object({
  toelichting: z.string().trim().max(500, 'Maximaal 500 tekens').optional(),
})

// ─── Zod: queries ────────────────────────────────────────────────────────────

export const BESTELADVIES_LIJST_WEERGAVE = ['OPEN', 'HISTORIE', 'ALLE'] as const
export type BesteladviesLijstWeergave = (typeof BESTELADVIES_LIJST_WEERGAVE)[number]

export const besteladviesListQuerySchema = z.object({
  /** OPEN = te beoordelen, HISTORIE = afgerond/vervallen, ALLE = beide. */
  weergave: z.enum(BESTELADVIES_LIJST_WEERGAVE).default('ALLE'),
  leverancierId: idParam('leverancierId').optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})

export interface BesteladviesLijstMetaDTO {
  page: number
  pageSize: number
  total: number
  /** Aantal open adviezen (ongeacht de weergave), voor de tab-teller. */
  aantalOpen: number
}

export const besteladviesDetailQuerySchema = z.object({
  regelStatus: z.enum(BESTELADVIES_REGEL_STATUS).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
})

export const minimumniveausQuerySchema = z.object({
  weergave: z.enum(MINIMUMNIVEAU_WEERGAVE).default('VOORSTELLEN'),
  zoek: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})

export const besteladviesArtikelOptiesQuerySchema = z.object({
  search: z.string().trim().max(100).optional(),
})
